import { createTrackerForCategory } from '@gonfalon/analytics/createTrackerForCategory';
import { isAccountLapsed, isTrialAccount, planInfo, subscriptionState } from '@gonfalon/constants/constants';
import { enablePlanPickerRedesign, selfServeCheckoutRecordedTermsVersion } from '@gonfalon/dogfood-flags/dogfood-flags';
import { trackError } from '@gonfalon/telemetry/trackError';

import type { BillingEventInfo, CancelFormFields, TrialBannerState, TrialSource } from './types';

export const trackBillingEvent = (name: string, eventData?: BillingEventInfo) => {
  const customerInfoWithEventData = {
    isAccountLapsed: isAccountLapsed(),
    isTrialAccount: isTrialAccount(),
    planInfo: planInfo(),
    subscriptionState: subscriptionState(),
    ...eventData,
  };

  return createTrackerForCategory('Billing')(name, customerInfoWithEventData);
};

export const trackTrialStatusCTAClicked = (daysLeftInTrial: number, cta: string) => {
  trackBillingEvent('Trial Status CTA Clicked', { daysLeftInTrial, cta });
};

export const trackPlanPickerLoaded = (daysLeftInTrial: number) => {
  const isNewPlanPicker = enablePlanPickerRedesign();
  trackBillingEvent('Billing Plan Picker Page Loaded', {
    daysLeftInTrial,
    isNewPlanPicker,
  });
};

export const trackPlanComparisonButtonClick = (component: string) =>
  trackBillingEvent('Plan Comparison Button Clicked', { component });

export const trackProceedToCheckoutButtonClicked = (daysLeftInTrial?: number) => {
  const isNewPlanPicker = enablePlanPickerRedesign();
  trackBillingEvent('Proceed To Checkout Button Clicked', {
    daysLeftInTrial,
    isNewPlanPicker,
  });
};

export const trackTrialStatusPurchasePlanClicked = (daysLeftInTrial: number) =>
  trackBillingEvent('Trial Status Purchase Plan Clicked', { daysLeftInTrial });

export const trackFoundationAnnualExceedMonthlyEntitlementsClicked = () =>
  trackBillingEvent('Foundation Plan Annual Exceed Monthly Entitlements Link Clicked');

export const trackFoundationPlanCustomizeLimitsClicked = () =>
  trackBillingEvent('Foundation Plan Customize Limits Clicked');

export const trackEditPlanDetailsClicked = () =>
  trackBillingEvent('Plan Confirmation Edit Plan Details Button Clicked');

export const trackExtendMyEnterpriseTrialClicked = () => trackBillingEvent('Extend My Enterprise Trial Clicked');

export const trackExtendMyGuardianTrialClicked = () => trackBillingEvent('Extend My Guardian Trial Clicked');

export const trackSelfServeTrialExtensionCTAClicked = (daysLeftInTrial: number) =>
  trackBillingEvent('Self Serve Trial Extension CTA Clicked', { daysLeftInTrial });

export const trackSelfServeExtendTrialClicked = (daysLeftInTrial: number, extensionDays: number) =>
  trackBillingEvent('Self Serve Extend Trial Clicked', { daysLeftInTrial, extensionDays });

export const trackSelfServeTrialExtensionConfirmed = (daysLeftInTrial: number, extensionDays: number) =>
  trackBillingEvent('Self Serve Trial Extension Confirmed', { daysLeftInTrial, extensionDays });

export const trackSelfServeTrialExtensionPopoverClosed = (daysLeftInTrial: number) =>
  trackBillingEvent('Self Serve Trial Extension Popover Closed', { daysLeftInTrial });

export const trackSelfServeTrialExtensionModalClosed = (daysLeftInTrial: number, extensionDays: number) =>
  trackBillingEvent('Self Serve Trial Extension Modal Closed', { daysLeftInTrial, extensionDays });

export const trackSelfServeTrialBannerViewed = ({
  daysLeftInTrial,
  extensionCount,
  trialBannerState,
  trialSource,
}: {
  daysLeftInTrial: number;
  extensionCount: number;
  trialBannerState: TrialBannerState;
  trialSource: TrialSource;
}) =>
  trackBillingEvent('Self Serve Trial Banner Viewed', {
    daysLeftInTrial,
    extensionCount,
    trialBannerState,
    trialSource,
  });

export const trackRetrialBannerViewed = ({
  daysSinceTrialEnd,
  hasPriorCampaign,
}: {
  daysSinceTrialEnd?: number;
  hasPriorCampaign: boolean;
}) => trackBillingEvent('Re-trial Banner Viewed', { daysSinceTrialEnd, hasPriorCampaign });

export const trackRetrialCTAClicked = ({ component, cta }: { component: string; cta: string }) =>
  trackBillingEvent('Re-trial CTA Clicked', { component, cta });

export const trackRetrialBannerDismissed = () => trackBillingEvent('Re-trial Banner Dismissed');

export const trackSubscriptionCanceled = (reasons: CancelFormFields) =>
  trackBillingEvent('Subscription canceled', reasons);

export const trackSubscriptionCancelError = (error: Error, reasons: CancelFormFields) => {
  const e = new Error(error.message);
  e.name = 'SubscriptionCancelError';
  e.cause = error;

  // this goes to datadog
  trackError(e, { severity: 'high', owner: 'team-billing' });

  // this goes to segment then snowflake, etc.
  trackBillingEvent('Subscription cancel error', { error: error.message, ...reasons });
};

export const trackSelfServeCheckoutTermsAccepted = () =>
  trackBillingEvent('Self Serve Checkout Terms Accepted', { termsVersion: selfServeCheckoutRecordedTermsVersion() });

export const trackSavePaymentButtonClicked = () => trackBillingEvent('Save Payment Button Clicked');

export const trackUpdateSubscriptionButtonClick = ({
  selectedSubscription,
  selectedSubscriptionPrice,
  subscription,
  version,
  planKey,
  bundleKey,
}: {
  selectedSubscription?: unknown;
  version?: number;
  planKey?: string;
  bundleKey?: string;
  selectedSubscriptionPrice?:
    | { prices?: { monthly?: number; yearly?: number } }
    | { getYearlyPrice?: () => number; getMonthlyPrice?: () => number }
    | null;
  subscription: unknown;
}) => {
  let price;

  const subscriptionData = subscription as { billingProcess?: string; planType?: string } | null;

  // For usage-based billing plans, don't try to get the price
  if (
    subscriptionData?.billingProcess === 'USAGE_BASED_BILLING' ||
    subscriptionData?.billingProcess === 'COMMIT_WITH_OVERAGE'
  ) {
    price = null;
  }

  // Handle both modern and legacy selectedSubscription types
  const selectedSubscriptionData = selectedSubscription as {
    planType?: string | null;
    getSeatCount?: () => number;
    hasSSO?: () => boolean;
    isYearly?: () => boolean;
  } | null;

  // Only calculate price if it's not already set to null for usage-based billing
  if (selectedSubscriptionPrice && price !== null) {
    // Handle modern PreviewPriceRep format
    if ('prices' in selectedSubscriptionPrice && selectedSubscriptionPrice.prices) {
      if (selectedSubscriptionData?.isYearly?.() && selectedSubscriptionPrice.prices.yearly) {
        price = selectedSubscriptionPrice.prices.yearly / 100; // Convert cents to dollars
      } else if (selectedSubscriptionPrice.prices.monthly) {
        price = selectedSubscriptionPrice.prices.monthly / 100; // Convert cents to dollars
      }
    }
    // Handle legacy SubscriptionPrice format
    else if ('getYearlyPrice' in selectedSubscriptionPrice) {
      if (selectedSubscriptionData?.isYearly?.()) {
        price = selectedSubscriptionPrice.getYearlyPrice?.();
      } else {
        price = selectedSubscriptionPrice.getMonthlyPrice?.();
      }
    }
  }

  trackBillingEvent('Update Subscription Button Clicked', {
    seatCount: selectedSubscriptionData?.getSeatCount?.(),
    hasSSO: selectedSubscriptionData?.hasSSO?.(),
    price,
    planType: selectedSubscriptionData?.planType ?? subscriptionData?.planType,
    version,
    planKey,
    bundleKey,
    oldPlan: subscription,
  });
};

export const trackPlanPickerButtonClick = (component: string, cta: string) =>
  trackBillingEvent('Plan Picker Button Clicked', { component, cta });
